import { FieldType } from '@frontend/app/types/globalTypes';
import { ComboOperator, ComparisonOperator } from '@frontend/app/types/automations/condition/Operator';
import {
  ArrayConditionOperators,
  BooleanConditionOperators,
  DateConditionOperators,
  NumberConditionOperators,
  TextConditionOperators,
} from '@frontend/app/types/automations/condition/ConditionOperator';
import { format } from 'date-fns';

/**
 * Maps positive operators to their negative equivalents
 */
export const NEGATIVE_OPERATOR_MAPPING: Record<string, string> = {
  'EQUALS': ComboOperator.NOT_EQUALS,
  'CONTAINS': ComboOperator.NOT_CONTAINS,
  'HAS_VALUE': ComboOperator.IS_NULL,
  'STARTS_WITH': ComboOperator.NOT_STARTS_WITH,
  'ENDS_WITH': ComboOperator.NOT_ENDS_WITH,
  'IN_THE_LAST': ComboOperator.NOT_IN_THE_LAST,
  'IN_THE_NEXT': ComboOperator.NOT_IN_THE_NEXT,
  'MORE_THAN_AGO': ComboOperator.NOT_MORE_THAN_AGO,
};

/**
 * Gets the appropriate condition operators based on field type
 */
export const getConditionOperatorsByFieldType = (
  fieldType: FieldType,
  operator: string
) => {
  switch (fieldType) {
    case FieldType.ANNUAL:
    case FieldType.DATE:
      return DateConditionOperators.find((op) => op.value === operator);
    case FieldType.ARRAY:
      return ArrayConditionOperators.find((op) => op.value === operator);
    case FieldType.BOOLEAN:
      return BooleanConditionOperators.find((op) => op.value === operator);
    case FieldType.NUMBER:
    case FieldType.PERCENTAGE:
      return NumberConditionOperators.find((op) => op.value === operator);
    case FieldType.TEXT:
    case FieldType.EMAIL:
      return TextConditionOperators.find((op) => op.value === operator);
    default:
      return null;
  }
};

/**
 * Formats a condition value for display
 */
export const formatConditionValue = (
  value: unknown,
  operator: string,
  field?: { choices?: Array<{ value: unknown; displayName: string }>; attributeType?: FieldType }
): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (operator === ComparisonOperator.HAS_VALUE) {
    return 'has value';
  }

  if (operator === ComboOperator.IS_NULL) {
    return 'has no value';
  }

  // Handle BETWEEN operator
  if (operator === ComboOperator.BETWEEN && Array.isArray(value) && value.length === 2) {
    const formatSingleValue = (val: unknown): string => {
      if (field?.attributeType === FieldType.DATE && typeof val === 'string') {
        try {
          const date = new Date(val);
          if (!isNaN(date.getTime())) {
            return format(date, 'MMM do, yyyy');
          }
        } catch (error) {
          // Fall back to original value
        }
      }

      if (field?.choices && val) {
        const choice = field.choices.find((c) => c.value === val);
        return choice?.displayName || val.toString();
      }

      return val?.toString() || '';
    };

    return `${formatSingleValue(value[0])} and ${formatSingleValue(value[1])}`;
  }

  // Handle date formatting
  if (field?.attributeType === FieldType.DATE && typeof value === 'string') {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return format(date, 'MMM do, yyyy');
      }
    } catch (error) {
      // Fall back to original value if date parsing fails
    }
  }

  if (field?.choices && value) {
    // Handle single values that should be mapped through choices
    // This fixes the issue where project IDs are shown instead of project names
    const choice = field.choices.find((c) => c.value == value); // Use == for type coercion
    return choice?.displayName || value.toString();
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    if (field?.choices) {
      return value
        .map((val) => field.choices?.find((c) => c.value == val)?.displayName || val) // Use == for type coercion
        .join(', ');
    }
    return value.join(', ');
  }

  return value.toString();
};

/**
 * Gets the negated version of an operator
 */
export const getNegatedOperator = (operator: string): string => {
  return NEGATIVE_OPERATOR_MAPPING[operator] || operator;
};
